// Smart Sash Admin Panel - Main App Component
import { getDefaultRouteForRoles } from '@/config/permissions';
import { usePermissions } from '@/hooks/usePermissions';
import { AuthProvider, useAuth } from '@/services/auth';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

// Layout
import { MainLayout } from '@/components/layout/MainLayout';
import { PublicVersionBadge } from '@/components/layout/PublicVersionBadge';

// Auth Components
import { AuthCallback } from '@/components/auth/AuthCallback';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';

// Notifications
import { NotificationContainer } from '@/components/notifications';

// Pages
import { HoodUsageReport } from '@/components/reports';
import GroupManagement from '@/pages/protected/GroupManagement';
import Certification from '@/pages/protected/Certification';
import { Dashboard } from '@/pages/protected/Dashboard';
import DeviceManagement from '@/pages/protected/DeviceManagement';
import InstitutionManagement from '@/pages/protected/InstitutionManagement';
import LabManagement from '@/pages/protected/LabManagement';
import RfidTags from '@/pages/protected/RfidTags';
import Streams from '@/pages/protected/Streams';
import StreamViewer from '@/pages/protected/StreamViewer';
import UserManagement from '@/pages/protected/UserManagement';
import UserPermissionManagement from '@/pages/protected/UserPermissionManagement';
import UserScopeManagement from '@/pages/protected/UserScopeManagement';
import { ChangePassword } from '@/pages/public/ChangePassword';
import { DeviceRegistration } from '@/pages/public/DeviceRegistration';
import { Login } from '@/pages/public/Login';
import { RFIDAssignment } from '@/pages/public/RFIDAssignment';

/**
 * Role guard wrapper that looks up allowed roles from the permissions config.
 */
const RoleGuard: React.FC<{ path: string; children: React.ReactNode }> = ({ path, children }) => {
  const { canAccessRoute } = usePermissions();
  const { user } = useAuth();

  if (!canAccessRoute(path)) {
    const defaultRoute = getDefaultRouteForRoles(user?.roles ?? []);
    return <Navigate to={defaultRoute} replace />;
  }

  return <>{children}</>;
};

/**
 * Main App Component
 */
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        {/* Global Notification Container */}
        <NotificationContainer />
        <PublicVersionBadge />

        <Routes>
          {/* Public, session-scoped onboarding routes intentionally bypass app RBAC. */}
          <Route path="/login" element={<Login />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/rfid-assignment" element={<RFIDAssignment />} />
          <Route path="/device-registration" element={<DeviceRegistration />} />
          <Route path="/auth/callback" element={<AuthCallback />} />

          {/* Protected Routes with Main Layout */}
          <Route
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route
              path="/dashboard"
              element={
                <RoleGuard path="/dashboard">
                  <Dashboard />
                </RoleGuard>
              }
            />
            <Route
              path="/institutions"
              element={
                <RoleGuard path="/institutions">
                  <InstitutionManagement />
                </RoleGuard>
              }
            />
            <Route
              path="/groups"
              element={
                <RoleGuard path="/groups">
                  <GroupManagement />
                </RoleGuard>
              }
            />
            <Route
              path="/users"
              element={
                <RoleGuard path="/users">
                  <UserManagement />
                </RoleGuard>
              }
            />
            <Route
              path="/users/:institutionId/:userId/scopes"
              element={
                <RoleGuard path="/users/:userId/scopes">
                  <UserScopeManagement />
                </RoleGuard>
              }
            />
            <Route
              path="/users/:institutionId/:userId/permissions"
              element={
                <RoleGuard path="/users/:userId/permissions">
                  <UserPermissionManagement />
                </RoleGuard>
              }
            />
            <Route
              path="/devices"
              element={
                <RoleGuard path="/devices">
                  <DeviceManagement />
                </RoleGuard>
              }
            />
            <Route
              path="/labs"
              element={
                <RoleGuard path="/labs">
                  <LabManagement />
                </RoleGuard>
              }
            />
            <Route
              path="/certifications"
              element={
                <RoleGuard path="/certifications">
                  <Certification />
                </RoleGuard>
              }
            />
            <Route
              path="/admin/rfid-tags"
              element={
                <RoleGuard path="/admin/rfid-tags">
                  <RfidTags />
                </RoleGuard>
              }
            />
            <Route
              path="/streams"
              element={
                <RoleGuard path="/streams">
                  <Streams />
                </RoleGuard>
              }
            />
            <Route
              path="/streams/:sessionId"
              element={
                <RoleGuard path="/streams">
                  <StreamViewer />
                </RoleGuard>
              }
            />
            <Route
              path="/reports/hood-usage"
              element={
                <RoleGuard path="/reports/hood-usage">
                  <HoodUsageReport />
                </RoleGuard>
              }
            />
          </Route>

          {/* Default Route */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          {/* Catch-all - redirect to dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
